import Backbone from 'backbone';
import projectionGrid from 'projection-grid';
import _ from 'underscore';
import { projectionGridScopeNavigator } from '@bingads-webui-campaign/projection-grid-scope-navigator';
import { crossPageSelectAllPlugin, crossPageSelectHeadRow } from '@bingads-webui/projection-grid-cross-page-select-all';
import { placeholderPlugin } from '@bingads-webui/projection-grid-placeholder';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { a11yPlugin } from '@bingads-webui/projection-grid-a11y';
import { assistedSelectionPlugin } from '@bingads-webui/projection-grid-assisted-selection';
import { mapColumnsConfig } from '@bingads-webui/projection-grid-base-column';
import template from './template.pug';
import * as PredefinedColumns from './column';

export class LocationRecommendationGrid extends Backbone.View {
  get events() {
    return {
      'click span.dismiss-recommendation': _.bind(this.onDismissClick, this),
    };
  }

  initialize({
    deps,
    columns = [
      'Campaign',
      'Location',
      'Impression',
      'Dismiss',
    ],
    columnOverrider = {},
    hasPagination = true,
    hasSelection = true,
    dataSource = {
      type: 'memory',
      primaryKey: 'OpportunityId',
      data: [],
    },
    pageSize = 10,
    scope = {},
    selectAllByDefault = false,
    tableClasses = ['table', 'table-bordered', 'table-hover'],
    isForVNext,
    gridRemoveAll,
  }) {
    this.tableClasses = tableClasses;
    this.selectAllByDefault = selectAllByDefault;
    this.deps = deps;
    this.hasPagination = hasPagination;
    this.hasSelection = hasSelection;
    this.dataSource = dataSource;
    this.pageSize = pageSize;
    this.isForVNext = isForVNext;
    this.columns = _.chain(columns)
      .map(col => PredefinedColumns[col])
      .compact()
      .value();
    this.columns = mapColumnsConfig(this.columns, {
      deps, scope, columnOverrider, isForVNext, gridRemoveAll,
    });
    this.gridRemoveAll = gridRemoveAll;
  }

  onRemoveAllRecommendationClick() {
    this.trigger('removeAllRecommendation');
  }

  onDismissClick(event) {
    const recommendation = this.grid.gridView.itemOfElement(event.target);
    this.trigger('dismiss', recommendation);
    return false;
  }

  mouseOverItem(event) {
    const recommendation = this.grid.gridView.itemOfElement(event.target);
    this.trigger('mouseover:recommendation', recommendation);
    return false;
  }

  mouseOutItem(event) {
    const recommendation = this.grid.gridView.itemOfElement(event.target);
    this.trigger('mouseout:recommendation', recommendation);
    return false;
  }

  render() {
    this.$el.html(template({
      hasPagination: this.hasPagination,
      isForVNext: this.isForVNext,
    }));
    let gridFactory = projectionGrid
      .factory({ vnext: true })
      .use(placeholderPlugin)
      .use(uiBlockerPlugin)
      .use(a11yPlugin)
      .use(assistedSelectionPlugin);
    const headRows = ['column-header-rows'];
    if (this.hasPagination) {
      gridFactory = gridFactory.use(paginationPlugin)
        .use(crossPageSelectAllPlugin);
      headRows.unshift(crossPageSelectHeadRow());
    }

    if (this.deps.isForVNext) {
      gridFactory = gridFactory.use(projectionGridScopeNavigator);
    }

    this.grid = gridFactory.create({
      name: 'location-recommendation-grid',
      el: this.$('.location-grid-container'),
      dataSource: this.dataSource,
      tableClasses: this.tableClasses,
      rows: {
        headRows,
      },
      events: this.gridRemoveAll ? {
        'mouseover tbody': _.bind(this.mouseOverItem, this),
        'mouseout tbody': _.bind(this.mouseOutItem, this),
        'click .recommendation-target-grid__removeall-header a': _.bind(this.onRemoveAllRecommendationClick, this),
      } : {
        'mouseover tbody': _.bind(this.mouseOverItem, this),
        'mouseout tbody': _.bind(this.mouseOutItem, this),
      },
      columns: this.columns,
      plugins: {
        a11y: { i18n: this.deps.i18n },
        crossPageSelectAll: {
          i18n: this.deps.i18n,
        },
        placeholder: {
          container: this.$('.placeholder-container'),
          text: this.deps.i18n.getString('LocationGrid_Content_No_Recommendation'),
        },
        pagination: {
          el: this.$('.grid-pager.pager'),
          pageSize: this.pageSize,
          availablePageSizes: [10, 20, 50, 100],
          enableRandomPage: false,
        },
        projectionGridScopeNavigator: this.deps.scope,
      },
      query: {
        orderby: [{
          direction: -1,
          key: 'EstimatedIncreaseInImpressions',
        }],
      },
      i18n: this.deps.i18n,
      sortableHeader: {
        name: 'Impression',
        directionIndex: 1,
      },
      selection: this.hasSelection ? {
        enabled: true,
        headClasses: ['text-center'],
        bodyClasses: ['text-center'],
      } : false,
    });

    this.grid.gridView.render();

    if (this.selectAllByDefault) {
      this.grid.gridView.once('didReload', () => {
        _.delay(() => this.grid.gridView.selectAll(), 10);
      });
    }

    this.grid.gridView.on('didSelect', () => {
      this.trigger('selectChanged', this.grid.gridView.selectedItems());
      this.trigger('isAllSelected', !!(this.grid.crossPageSelectAll && this.grid.crossPageSelectAll.isAllSelected));
    });

    this.grid.gridView.on('didReload', () => {
      _.delay(() => {
        this.trigger('display:recommendations', this.grid.gridView.itemArray);
      }, 10);
    });

    this.on('update:data', (data) => {
      this.grid.dataSource.data = data;
      if (this.selectAllByDefault) {
        this.grid.gridView.once('didReload', () => {
          _.delay(() => this.grid.gridView.selectAll(), 10);
        });
      } else { // work around to remove select cross page banner
        this.grid.gridView.once('didReload', () => {
          _.delay(() => {
            this.grid.gridView.selectAll();
            this.grid.gridView.deselectAll();
          }, 10);
        });
      }

      this.grid.gridView.refresh(true);
    });

    return this;
  }
}
